import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { Rush, RUSH, STD, Top, TOPPRIORITY } from 'src/app/features/guided-order/samples/constants/sample-constants';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContactType } from '../enums/contact-type.enum';
import { FilterRequest } from '../models/filter-request.model';
import { FavoriteOrderDetailsResponse } from '../models/my-orders/favorite-order-details-response.model';
import { OrderDetailsResponse } from '../models/my-orders/order-details-response.model';
import { OrderResponse } from '../models/my-orders/order-response.model';
import { PreviousSamplesRequest } from '../models/previous-samples-request.model';
import { PriorityLevel } from '../models/priority-level.model';
import { Recipient } from '../models/recipient.model';
import { SampleResponse } from '../models/sample-response.model';
import { SubmittedOrderResponse, PendingOrderResponse } from '../models/full-order-details.model';
import { TestParameterRequest } from '../models/specification/test-parameter-request.model';
import { OrderShippingDetailsResponse } from '../models/shipping/shipping-details-response.model';
import { DocumentType } from '../enums/document-type.enum';
import { DeliveryChannel } from '../enums/delivery-channel.enum';
import { AdvanceSearchRequest } from '../models/advance-search/advance-search-request.model';
import { AdvanceSearchResponse } from 'projects/dop-ordering/src/lib/models/advance-search/advance-search-response.model';
import { ProductApplicabilityResponseModel } from '../models/legacy-matrix-applicability/product-applicability-response.model';
import { ProductApplicabilityRequestModel } from '../models/legacy-matrix-applicability/product-applicability-request.model';
import { ProductApplicabilityScRequest } from '../models/legacy-matrix-applicability/product-applicability-sc-request.model';
import { MonographSalesCatalogue } from '../models/monograph/monograph-sales-catalogue.model';
import { SampleInvoiceableItemsResponse } from '../models/sample-invoiceable-items-response.model';
import { Unit } from '../models/specification/units.model';
import { SettingsService } from './settings.service';
import { SampleQuantityResponse } from '../models/required-sample-quantity/sample-quantity-response.model';
import { AccountsOrderingServiceResponse } from '../models/order-creation/accounts-ordering-service-response.model';
import { AdditionalField } from '../models/additional-field.model';
import { ShippingEstimatesRequest } from '../models/logistic/shipping-estimates-request.model';
import { ShippingEstimatesResponse } from '../models/logistic/shipping-estimates-response.model';
import { ProcessShipmentResponse } from '../models/logistic/process-shipment-response.model';
import { ProcessShipmentRequest } from '../models/logistic/process-shipment-request.model';
import { ShipTo } from '../models/logistic/ship-to.model';
import { LogisticAddress } from '../models/logistic/address.model';
import { ShippingPackages } from '../models/logistic/shipping-packages.model';
import { OrderShippingPackage } from '../models/shipping/shipping-package-request.model';
import { WeightUnits } from '../models/logistic/weight-units.model';
import { SizeUnit } from '../models/logistic/size-unit.model';
import { ShipFrom } from '../models/logistic/ship-from.model';
import { PickUpInformation } from '../models/logistic/pickup-information.model';
import { CurrentOrderService } from '../services/current-order.service';
import { CurrentOrder } from '../models/current-order.model';
import { CountryService } from './country.service';
import { LabModel } from '../models/lab.model';
import { HelperService } from '../services/helper.service';
import { OrderShippingTrackingDetail } from '../models/logistic/order-shipping-tracking-detail.model';
import { PackageTrackingInformation } from '../models/logistic/package-tracking-information.model';
import JSZip from 'jszip';
import { Label } from '../models/logistic/label.model';
import { OrderShippingTrackingPackageDetail } from '../models/logistic/order-shipping-tracking-package-detail.model';
import { OrderCommandService } from './order-command.service';
import { ReportTemplate } from '../models/report-template.model';
import { GetAcfRecipientsResponse } from '../models/getAcfRecipientsResponse.model';
import { InformationToSend } from '../models/information-to-send.model';
import { UserAndAccountSpecificDetails } from '../models/user-and-account-specific-details';
import { CiGroupType } from '../enums/ci-group-type.enum';
import { ScheduleOrderDetailsResponse } from '../models/my-orders/schedule-order-details-reponse.model';

@Injectable({
  providedIn: 'root'
})
export class OrderDataService {

  orderQueryApiBaseUrl: string;
  lab: LabModel;
  packageIdMapping: Map<number, number[]> = new Map<number, number[]>();

  get currentOrder(): CurrentOrder {
    return this.currentOrderService.currentOrder;
  }

  constructor(
    private httpClient: HttpClient,
    private appConfigService: AppConfigService,
    private settingService: SettingsService,
    private currentOrderService: CurrentOrderService,
    private countryService: CountryService,
    private helperService: HelperService,
    private orderCommandService: OrderCommandService) {
    this.orderQueryApiBaseUrl = this.appConfigService.configuration?.apiConfig.orderQueryBaseUrl;
  }

  getQuotationsForAccount(accountCode: string, cache = true): Observable<any> {
    const url = `accounts/${accountCode}/quotations`;
    return this.getData<any>(url, cache);
  }

  getQuotedItems(accountCode: string, quotationCode: string, cache = true): Observable<any> {
    const apiUrl = `accounts/${accountCode}/Quotations/${quotationCode}/quotedItems`;
    return this.getData<any>(apiUrl, cache);
  }

  getSortedQuotedItems(accountCode: string, quotationCode: string, cache = true): Observable<any> {
    const apiUrl = `accounts/${accountCode}/Quotations/${quotationCode}/sortedQuotedItems`;
    return this.getData<any>(apiUrl, cache);
  }

  getProductMatrices(quotationCode: string, cache = true): Observable<any> {
    const apiUrl = `productsTrees/quotations/${quotationCode}/productMatrix`;
    return this.getData<any>(apiUrl, cache);
  }

  getUnits(cache = true) {
    const apiUrl = `units`;
    return this.getData<Unit[]>(apiUrl, cache).pipe(map(units => {
      if (this.settingService.Configurations.FrequentlyUsedNumUnits && this.settingService.Configurations.FrequentlyUsedNumUnits.length > 0) {
        this.settingService.Configurations.FrequentlyUsedNumUnits.forEach((x, index) => {
          const temp = units.find(y => y.code === x);
          if (temp) {
            temp.isFrequentlyUsedNumUnits = true;
            temp.serialNumber = index;
          }
        });
      }

      if (this.settingService.Configurations?.FrequentlyUsedDenUnits && this.settingService.Configurations?.FrequentlyUsedDenUnits.length > 0) {
        this.settingService.Configurations.FrequentlyUsedDenUnits.forEach((x, index) => {
          const temp = units.find(y => y.code === x);
          if (temp) {
            temp.isFrequentlyUsedDenUnits = true;
            temp.serialNumber = index;
          }
        });
      }

      return units;
    }));
  }

  getTestDetails(testCode: string, loading = true) {
    const apiUrl = `commercialItems/${testCode}/tests`;
    return this.getData<any>(apiUrl, true, loading);
  }

  getPackageDetails(packageCode: string, loading = true) {
    const apiUrl = `commercialItems/${packageCode}/packages`;
    return this.getData<any>(apiUrl, true, loading);
  }

  getTestBundleDetails(testBundleCode: string, loading = true) {
    const apiUrl = `commercialItems/${testBundleCode}/testbundles`;
    return this.getData<any>(apiUrl, true, loading);
  }

  getTestQuotedItems(accountCode: string, quotationLineCode: string, cache = true, loading = true) {
    const apiUrl = `accounts/${accountCode}/quotationLines/${quotationLineCode}/test`;
    return this.getData<any>(apiUrl, cache, loading);
  }

  getTestParameters(codes: string[], loading = true) {
    const request = this.CreateParamterRequestObject(codes);
    const apiUrl = `testParameters`;
    return this.postData(request, apiUrl, loading);
  }

  getSampleInvoiceableItems(codes: string[]): Observable<SampleInvoiceableItemsResponse[]> {
    const apiUrl = `samples/invoiceableItems/query`;
    return this.postData<any>(codes.map(String), apiUrl);
  }

  getParameterListValue(request: TestParameterRequest) {
    const apiUrl = `commercialItems/parameters`;
    return this.postData(request, apiUrl);
  }

  getPackageQuotedItems(accountCode: string, quotationLineCode: string, cache = true, loading = true) {
    const apiUrl = `accounts/${accountCode}/quotationLines/${quotationLineCode}/package`;
    return this.getData<any>(apiUrl, cache, loading);
  }

  getSubGroupQuotedItems(accountCode: string, subgroupCode: string, isCustomerPackage: boolean, cache = true, loading = true) {
    const apiUrl = isCustomerPackage
      ? `accounts/${accountCode}/quotations/customerPackages/${subgroupCode}`
      : `accounts/${accountCode}/quotations/subGroups/${subgroupCode}`;
    return this.getData<any>(apiUrl, cache, loading);
  }

  getOrder(orderCode: string, cache = true): Observable<OrderResponse> {
    const url = `orders/${orderCode}`;
    return this.getData<any>(url, cache);
  }

  getSalesCatalogsAndMemberDetails(labSCode: string, accountCode: string, quotationCode: string): Observable<any>{
    let apiUrl = `labSCode/${labSCode}/account/${accountCode}/quotation/${quotationCode}/salesCatalogues`;
    return this.getData<any>(apiUrl);
  }

  getContractsByAccountAndQuotation(accountCode: string, quotationCode: string, cache = true): Observable<any> {
    const apiUrl = `accounts/${accountCode}/quotations/${quotationCode}/contracts`;
    return this.getData<any>(apiUrl, cache);
  }

  getExternalLimsContractsByAccount(accountCode: string, cache = false): Observable<any> {
    const apiUrl = `accounts/${accountCode}/ExternalLimscontracts`;
    return this.getData<any>(apiUrl, cache);
  }

  getLabType(code: string, cache = true): Observable<any> {
    const apiUrl = `labSites/${code}`;
    return this.getData<any>(apiUrl, cache);
  }

  getNIMSRecipient(accountCode: string, isElimsLab: boolean, quotationcode: string, acfCode : string)
    : Observable<Recipient[]> {
      var apiUrl;
      if(isElimsLab){
        apiUrl = `labtype/ELIMS/acfCode/${acfCode}/eLIMSrecipients?accountCode=${accountCode}`
      }
      else{
        apiUrl = `accounts/${accountCode}/labtype/NIMS/recipients?quotationcode=${quotationcode}`;
      }
    return this.getData<RecipientDto[]>(apiUrl).pipe(map(dto => {
      if (dto) {
        const recipients: Recipient[] = [];
        dto.forEach(element => {
          if ((element.informationToSend.some(x => DocumentType[x.documentType] === DocumentType[DocumentType.AnalyticalReportCopy])
            || element.informationToSend.some(x => DocumentType[x.documentType] === DocumentType[DocumentType.InvoiceDocument])
            || element.informationToSend.some(x => DocumentType[x.documentType] === DocumentType[DocumentType.AnalyticalReport]))
            && ((element.lastName && element.lastName.trim() !== '') || (element.firstName && element.firstName.trim() !== ''))) {
            recipients.push(new Recipient(element.id, element.code, element.firstName, element.lastName,
              element.email, true, element.informationToSend.some(x => DocumentType[x.documentType]
                === DocumentType[DocumentType.InvoiceDocument]),
              element.informationToSend.some(x => DocumentType[x.documentType] === DocumentType[DocumentType.AnalyticalReport]),
              element.informationToSend.some(x => DocumentType[x.documentType] === DocumentType[DocumentType.AnalyticalReportCopy]),
              ContactType.Configured));
          }
        });
        return recipients;
      }
    }));
  }

  getPendingOrder(request: FilterRequest): Observable<OrderDetailsResponse> {
    const url = this.orderQueryApiBaseUrl + `orders/pendings`;
    return this.httpClient.get<OrderDetailsResponse>(url, {
      params: {
        sortColumn: request.sortingColumn,
        pageNumber: request.pageNumber.toString(),
        pageSize: request.pageSize.toString(),
        searchTerm: request.searchTerm,
        sortDirection: request.sortDirection.toString()
      }
    });
  }

  getSubmittedOrder(request: FilterRequest): Observable<OrderDetailsResponse> {
    const url = this.orderQueryApiBaseUrl + `orders/submitted`;
    return this.httpClient.get<OrderDetailsResponse>(url, {
      params: {
        sortColumn: request.sortingColumn,
        pageNumber: request.pageNumber.toString(),
        pageSize: request.pageSize.toString(),
        searchTerm: request.searchTerm,
        sortDirection: request.sortDirection.toString()
      }
    });
  }

  getFavouriteOrder(request: FilterRequest): Observable<FavoriteOrderDetailsResponse> {
    const url = this.orderQueryApiBaseUrl + `favoriteOrder`;
    return this.httpClient.get<FavoriteOrderDetailsResponse>(url, {
      params: {
        sortColumn: request.sortingColumn,
        pageNumber: request.pageNumber.toString(),
        pageSize: request.pageSize.toString(),
        searchTerm: request.searchTerm,
        sortDirection: request.sortDirection.toString()
      }
    });
  }

  getFullPendingOrder(orderCode: string): Observable<PendingOrderResponse> {
    const url = this.orderQueryApiBaseUrl + `orders/pendingOrders/${orderCode}`;
    return this.httpClient.get<PendingOrderResponse>(url);
  }

  getConfirmedOrder(orderCode: string): Observable<SubmittedOrderResponse> {
    const url = this.orderQueryApiBaseUrl + `orders/submittedOrders/${orderCode}`;
    return this.httpClient.get<SubmittedOrderResponse>(url);
  }

  getFavoriteItems(quotationCode: string): Observable<any> {
    const apiUrl = `quotations/${quotationCode}/favoriteAnalyses`;
    return this.getData<any>(apiUrl);
  }

  getSampleQuantityDisplayFlag(labsiteCode : string): Observable<boolean> {
    const apiUrl = this.orderQueryApiBaseUrl +  `enableSampleQuantityDisplay/${labsiteCode}`;
    return this.httpClient.get<boolean>(apiUrl);
  }

  getPriorityLevels(): Observable<PriorityLevel[]> {
    const apiUrl = `priorityLevels`;
    return this.getData<PriorityLevel[]>(apiUrl).pipe(map((data: PriorityLevel[]) => {
      const prioritySequence: PriorityLevel[] = [];
      prioritySequence.push(data.filter(x => x.code === STD).shift());
      prioritySequence.push(data.filter(x => x.code === RUSH).map(x => { x.name = Rush; return x; }).shift());
      prioritySequence.push(data.filter(x => x.code === TOPPRIORITY).map(x => { x.name = Top; return x; }).shift());

      return prioritySequence;
    }));
  }

  getFavoriteSamples(accountCode: string, orderingServiceCode: string, request: FilterRequest): Observable<any> {
    let apiUrl = this.orderQueryApiBaseUrl + `accounts/${accountCode}/orderingServices/${orderingServiceCode}/favoriteSamples`;
    request.searchTerm = request.searchTerm.trim();
    if (request.searchTerm) {
      apiUrl = apiUrl + `/${request.searchTerm}`;
    }
    return this.httpClient.get(apiUrl, {
      params: {
        sortColumn: request.sortingColumn,
        pageNumber: request.pageNumber.toString(),
        pageSize: request.pageSize.toString(),
        searchTerm: request.searchTerm,
        sortDirection: request.sortDirection.toString()
      }
    });
  }

  getFavoriteSamplesBySampleCodes(sampleCodes: string[]): Promise<any> {
    const apiUrl = this.orderQueryApiBaseUrl + 'favoriteSamples';
    return new Promise((resolve) => {
      this.httpClient.post(apiUrl, sampleCodes).subscribe(data => {
        resolve(data);
      });
    });
  }

  getPreviousSamples(request: PreviousSamplesRequest): Observable<any> {
    const url = this.orderQueryApiBaseUrl + `samples/previous`;
    return this.httpClient.get<any>(url, {
      params: {
        accountCode: request.accountCode,
        orderTemplateCode: request.orderTemplateCode,
        quotationCode: request.quotationCode,
        contractCode: request.contractCode,
        validatedOnly: request.validatedOnly.toString(),
        sortColumn: request.sortingColumn,
        pageNumber: request.pageNumber.toString(),
        pageSize: request.pageSize.toString(),
        searchTerm: request.searchTerm.trim(),
        sortDirection: request.sortDirection.toString(),
      }
    });
  }

  getSampleBarcode(orderCode: string, printingServiceCode: string, startIndex?: number): Observable<Blob> {
    let apiUrl = this.orderQueryApiBaseUrl + `orders/${orderCode}/reportTemplates/${printingServiceCode}/sampleBarcodes`;
    if (startIndex) {
      apiUrl = apiUrl + `?startIndex=${startIndex}`;
    }
    return this.httpClient.get(apiUrl, { responseType: 'blob' });

  }

  getNewFavoriteSampleName(accountCode: string, orderingServiceCode: string): Observable<any> {
    const apiUrl = `accounts/${accountCode}/orderingServices/${orderingServiceCode}/favoriteSamples/new`;
    return this.getData<any>(apiUrl);
  }

  getIsFavoriteSampleNameExist(name: string, accountCode: string, orderingServiceCode: string): Observable<any> {
    const apiUrl = `accounts/${accountCode}/orderingServices/${orderingServiceCode}/favoriteSamples/${name}/isAlreadyTaken`;
    return this.getData<any>(apiUrl);
  }

  getResultConversions() {
    const apiUrl = `resultConversions`;
    return this.getData<any>(apiUrl);
  }

  getGuidedHelpContent(category: string, cache = true): Observable<any> {
    const apiUrl = `help-texts/${category}`;
    return this.getData<any>(apiUrl, cache);
  }

  getData<T>(apiUrl: string, cache = false, loading = true): Observable<T> {
    const url = this.orderQueryApiBaseUrl + apiUrl;

    let headers: HttpHeaders;
    headers = new HttpHeaders();
    if (cache) {
      headers = headers.append('cache-response', 'true');
    }
    if (!loading) {
      headers = headers.append('no-loading', 'true');
    }
    return this.httpClient.get<T>(url, { headers });
  }

  getSamples(orderCode: string): Promise<SampleResponse[]> {
    const apiUrl = `orders/${orderCode}/samples`;
    return new Promise((resolve) => {
      this.getData<any>(apiUrl).subscribe(data => {
        resolve(data);
      });
    });
  }

  getShippingDetails(orderCode: string): Observable<OrderShippingDetailsResponse> {
    return this.getData<OrderShippingDetailsResponse>(
      `orders/${orderCode}/shipping`);
  }

  getShippingLabels(orderCode: string): Observable<HttpResponse<Blob>> {
    const apiUrl = this.orderQueryApiBaseUrl + `orders/${orderCode}/shipping/label`;
    return this.httpClient.get<Blob>(apiUrl, { observe: 'response', responseType: 'blob' as 'json' });
  }

  getAdvanceSearchResult(quotationCode: string, request: AdvanceSearchRequest, loading = true): Observable<AdvanceSearchResponse[]> {
    const apiUrl = this.orderQueryApiBaseUrl + `quotations/${quotationCode}/quotationLines/search`;
    let headers: HttpHeaders;
    if (!loading) {
      headers = new HttpHeaders({ 'no-loading': 'true' });
    }
    return this.httpClient.post<AdvanceSearchResponse[]>(apiUrl, request, { headers });
  }

  getQuotableItemProductApplicability(request: ProductApplicabilityRequestModel): Observable<ProductApplicabilityResponseModel> {
    const apiUrl = 'products/quotedItems/applicability';
    return this.postData<ProductApplicabilityResponseModel>(request, apiUrl);
  }

  getSalesCatalogProductApplicability(request: ProductApplicabilityScRequest):
    Observable<ProductApplicabilityResponseModel> {
    const apiUrl = `products/accountCode/${request.accountCode}/labCode/${request.labSCode}/legacyMatrixCode/${request.legacyMatrixCode}/applicability`;
    return this.getData<ProductApplicabilityResponseModel>(apiUrl, true);
  }

  getMonographs(quotationCode: string, salesCatalogueCode: string): Observable<MonographSalesCatalogue> {
    const apiUrl = `quotation/${quotationCode}/salesCatalogues/${salesCatalogueCode}/monographs`;
    return this.getData<MonographSalesCatalogue>(apiUrl, true);
  }

  getRequiredSampleQuantity(orderCode: string): Observable<SampleQuantityResponse[]> {
    const apiUrl = `samples/${orderCode}/sampleQuantity`;
    return this.getData<SampleQuantityResponse[]>(apiUrl);
  }

  getRequiredSampleQuantityPDF(orderCode: string): Observable<Blob> {
    const apiUrl = this.orderQueryApiBaseUrl + `samples/${orderCode}/sampleQuantityPDF`;
    return this.httpClient.get(apiUrl, { responseType: 'blob' });
  }

  getOrderTemplatesForAccounts(accountCodes: string[], cache = true, loading = true): Observable<AccountsOrderingServiceResponse[]> {
    const apiUrl = `orderingServices?accountCodes=${accountCodes.join(',')}`;
    return this.getData<AccountsOrderingServiceResponse[]>(apiUrl, cache, loading);
  }

  getSampleAdditionalFields(orderingServiceCode: string, sampleServiceCode: string, cache = true): Observable<AdditionalField[]> {
    const apiUrl = `orderingServices/${orderingServiceCode}/sampleServices/${sampleServiceCode}/additionalFields`;
    return this.getData<AdditionalField[]>(apiUrl, cache);
  }

  getOrderAdditionalFields(orderingServiceCode: string, cache = true): Observable<AdditionalField[]> {
    const apiUrl = `orderingServices/${orderingServiceCode}/additionalFields`;
    return this.getData<AdditionalField[]>(apiUrl, cache);
  }

  getQuotedItemsPDF(accountCode: string, quotationCode: string): Observable<Blob> {
    const apiUrl = this.orderQueryApiBaseUrl + `accounts/${accountCode}/Quotations/${quotationCode}/quotedItemsAsPdf`;
    return this.httpClient.get(apiUrl, { responseType: 'blob' });
  }

  getTechnicalSalesDocumentPDF(comlimsCode: string, labsiteCode: string, ciGroupType: CiGroupType): Observable<Blob> {
    console.log(ciGroupType); // remove this console log once API starts accepting this param
    const apiUrl = this.orderQueryApiBaseUrl + `commercialItems/comlimsCode/${comlimsCode}/labSite/${labsiteCode}/technicalSalesDocument`;
    return this.httpClient.get(apiUrl, { responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return of(null);
      })
    );
  }

  getEstimates(request: ShippingEstimatesRequest) {
    const apiUrl = `shippingEstimates/getShipmentEstimates`;
    return this.postData<ShippingEstimatesResponse>(request, apiUrl);
  }

  getUserAndAccountSpecificDetails(accountCode: string): Observable<UserAndAccountSpecificDetails>{
    return this.getData<UserAndAccountSpecificDetails>(`userAndAccountSpecificDetails/${accountCode}`);
  }
  
  async processShipmentAndUpdateTracking(): Promise<ProcessShipmentResponse> {
    return new Promise(resolve => {
      this.processShipment().then(async (data) => {
        if (!data.masterTrackingNumber) {
          resolve(data);
        } else {
          await this.updateTrackingInformation(data).then(() => {
            resolve(data);
          });
        }
      }).catch(async () => {
        resolve(null);
      });
    });
  }

  async updateTrackingInformation(request: ProcessShipmentResponse) {
    const updateShippingTracking = new OrderShippingTrackingDetail();
    updateShippingTracking.masterTrackingNumber = request.masterTrackingNumber;
    updateShippingTracking.pickupTrackingNumber = request.pickupConfirmationNumber ?? null;
    updateShippingTracking.label = await this.getLabel(request.packages);
    updateShippingTracking.packages = this.getShippingTrackingPackage(request.packages);
    return this.orderCommandService.updateShippingTracking(this.currentOrder.OrderCode, updateShippingTracking).toPromise();
  }

  async getLabel(packages: PackageTrackingInformation[]): Promise<Label> {
    if (packages.length === 1) {
      return packages[0].label;
    } else {
      const zip = new JSZip();
      packages.forEach(async p => {
        zip.file(p.label.fileName, p.label.content, { base64: true });
      });
      const label = new Label();
      await zip.generateAsync({ type: 'base64' }).then(content => {
        label.content = content;
        label.fileName = 'shippingZip';
        label.contentType = 'application/zip';
      });
      return label;
    }
  }
  getShippingTrackingPackage(Packages: PackageTrackingInformation[]): OrderShippingTrackingPackageDetail[] {
    const orderShippingTrackingPackageDetail: OrderShippingTrackingPackageDetail[] = [];
    this.packageIdMapping.forEach((op, keys) => {
      const detail = new OrderShippingTrackingPackageDetail();
      detail.id = Number(keys);
      const packagesFound = Packages.filter(p => {
        return op.includes(p.packageId);
      });
      detail.trackingNumbers = packagesFound.map(pf => pf.trackingNumber).toString();
      orderShippingTrackingPackageDetail.push(detail);
    });
    return orderShippingTrackingPackageDetail;
  }
  
  async processShipment(): Promise<ProcessShipmentResponse> {
    const apiUrl = `shippingEstimates/processShipment`;
    const request = await this.formShipmentRequest();
    return this.postData<ProcessShipmentResponse>(request, apiUrl).toPromise();
  }

  getReportTemplates(accountCode: string, cache = true): Observable<ReportTemplate[]> {
    const apiUrl = `reportTemplates/${accountCode}`;
    return this.getData<ReportTemplate[]>(apiUrl, cache);
  }

  getScheduleOrders(request: FilterRequest): Observable<ScheduleOrderDetailsResponse> {
    const url = this.orderQueryApiBaseUrl + `recurrentOrdersSchedule`;
    return this.httpClient.get<ScheduleOrderDetailsResponse>(url, {
      params: {
        sortColumn: request.sortingColumn,
        pageNumber: request.pageNumber.toString(),
        pageSize: request.pageSize.toString(),
        searchTerm: request.searchTerm,
        sortDirection: request.sortDirection.toString()
      }
    });
  }
  
  private async formShipmentRequest(): Promise<ProcessShipmentRequest> {
    const processShipmentRequest = new ProcessShipmentRequest();
    processShipmentRequest.BillingCurrencyCode = this.currentOrder.shipping.orderShippingDetails.billingCurrencyCode;
    processShipmentRequest.CarrierServiceCode = this.currentOrder.shipping.orderShippingDetails.carrierServiceCode;
    processShipmentRequest.NotificationRequired = false;
    processShipmentRequest.OrderCode = this.currentOrder.OrderCode;
    processShipmentRequest.RequireDryIceShipment = this.currentOrder.shipping.orderShippingDetails.isDryIceShipment;
    processShipmentRequest.ReturnShipment = this.currentOrder.shipping.orderShippingDetails.isReturnShipmentOpted;
    processShipmentRequest.DryIceWeight = this.currentOrder.shipping.orderShippingDetails.dryIceWeight === null ?
    0 : this.currentOrder.shipping.orderShippingDetails.dryIceWeight;
    processShipmentRequest.NotificationLanguageCode = [];
    processShipmentRequest.PermitInformation = [];
    processShipmentRequest.PurchaseOrderNumber = [this.currentOrder.OrderReference];
    this.formShipFromRequest(processShipmentRequest);
    processShipmentRequest.ShipTo = await this.getShipTo();
    processShipmentRequest.ShippingPackage = this.getShippingPackages();
    processShipmentRequest.DryIceWeightUnit = this.currentOrder.shipping.orderShippingDetails.dryIceWeightUnit;
    processShipmentRequest.UseCustomsClearanceBroker = false;
    return processShipmentRequest;
  }

  private formShipFromRequest(processShipmentRequest: ProcessShipmentRequest) {
    const orderShippingPickup = this.currentOrder.shipping.orderShippingPickup;
    const shipFrom = new ShipFrom();

    shipFrom.accountCode = this.currentOrder.account.code;
    shipFrom.address = new LogisticAddress(orderShippingPickup.address.line1, orderShippingPickup.address.line2,
      orderShippingPickup.address.city, orderShippingPickup.address.state,
      orderShippingPickup.address.country, orderShippingPickup.address.zipCode, '', '');
    shipFrom.company = this.currentOrder.shipping.orderShippingPickup.address?.company;
    shipFrom.firstName = this.currentOrder.shipping.orderShippingPickup.address?.name;
    shipFrom.phone = this.currentOrder.shipping.orderShippingPickup.address?.phonenumber;
    shipFrom.shipmentDate = this.currentOrder.shipping.orderShippingPickup.shipmentDate;
    processShipmentRequest.ShipFrom = shipFrom;
    this.formPickupInformationRequest(processShipmentRequest);
  }

  private formPickupInformationRequest(processShipmentRequest: ProcessShipmentRequest) {
    const pickupInformation = new PickUpInformation();
    pickupInformation.earliestPickupTime = this.currentOrder.shipping.orderShippingPickup.earliestPickupTime;
    pickupInformation.latestPickupTime = this.currentOrder.shipping.orderShippingPickup.latestPickupTime;
    pickupInformation.pickupInstruction = this.currentOrder.shipping.orderShippingPickup.pickupComment;
    processShipmentRequest.ShipFrom.pickUpInformation = pickupInformation;
  }

  async getShipTo(): Promise<ShipTo> {

    if(this.currentOrder && this.currentOrder.quotation && this.currentOrder.quotation.receivingLabCode){
      await this.getLab();
    }

    const matchedCountry = this.countryService.getCountryLists()
      .find(e => e.name.toLowerCase() === this.lab?.country?.toLowerCase() ||
        e.code.toLowerCase() === this.lab?.country?.toLowerCase() ||
        'united states' === this.lab?.country?.toLowerCase() ||
        'united states of america' === this.lab?.country?.toLowerCase());

    if (this.lab) {
      const address = new LogisticAddress(this.lab.streetNumberAndStreet, '', this.lab.city,
      this.helperService.getStateCode(this.lab.state), matchedCountry?.code, this.lab.zipCode,
      this.lab.country, '');

      const shipTo = new ShipTo();
      shipTo.labCode = this.lab.code;
      shipTo.company = this.lab.name?.substring(0, 27); // Truncating to 27 characters
      shipTo.contactName = ''; // Do not have this info
      shipTo.phone = '6083322123'; // Do not have this info
      shipTo.address = address;

      return shipTo;
    }
  }

  getShippingPackages(): ShippingPackages[] {
    const shippingPackages: ShippingPackages[] = [];
    let packageId = 1;
    this.currentOrder.shipping.orderPackageInformation.forEach((op, keys) => {
      const processShipmentPackages: number[] = [];
      for (let quantityId = op.quantity; quantityId > 0; quantityId--) {
        processShipmentPackages.push(packageId);
        const shippingPackage = new ShippingPackages(packageId, 1, 50, true,
          this.parseSizeUnit(op), this.parseWeightUnit(op));
        shippingPackage.dimensions.height = op.height;
        shippingPackage.dimensions.length = op.length;
        shippingPackage.dimensions.width = op.width;
        shippingPackage.weight = op.weight;
        shippingPackages.push(shippingPackage);
        packageId++;
      }
      this.packageIdMapping.set(keys, processShipmentPackages);
    });
    return shippingPackages;
  }
  
  public async getLab(): Promise<LabModel> {
    return this.lab = await new Promise(async (resolve) => {
      this.getLabType(this.currentOrder.quotation.receivingLabCode)
        .subscribe((data: LabModel) => {
          resolve(data);
        });
    });
  }

  private parseWeightUnit(op: OrderShippingPackage): WeightUnits {
    return op.weightUnit === '1' ? WeightUnits.LB : WeightUnits.KG;
  }

  private parseSizeUnit(op: OrderShippingPackage): SizeUnit {
    return op.dimensionUnit === '1' ? SizeUnit.IN : SizeUnit.CM;
  }

  private postData<T>(data: any, apiUrl: string, loading = true): Observable<T> {
    const url = this.orderQueryApiBaseUrl + apiUrl;
    let headers: HttpHeaders;
    if (!loading) {
      headers = new HttpHeaders({ 'no-loading': 'true' });
    }
    return this.httpClient.post<T>(url, data, { headers });
  }

  private CreateParamterRequestObject(testCodes: string[]) {
    const parameterRequest = new TestParameterRequest();
    parameterRequest.codes = testCodes;
    return parameterRequest;
  }
}

export interface RecipientDto {
  informationToSend: {
    documentType: DocumentType,
    deliveryChannel?: DeliveryChannel,
    language?: string,
    noOfCopy?: string
  }[];
  id: string;
  code: string;
  contactType?: ContactType;
  title?: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  isReadOnly?: boolean;
}